import React from 'react'
import {
  Card,
  CardContent,
  CardActions,
  CardActionArea,
  Typography,
  Box,
  Chip,
  IconButton,
  Rating,
  Stack,
  Divider,
  CardMedia
} from '@mui/material'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Cloud as WeatherIcon,
  WineBar as PairingIcon,
  People as PeopleIcon,
  SmokingRooms as SmokingIcon
} from '@mui/icons-material'
import { SmokingSession } from '@store/api/sessionApi'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { getEffectiveSessionRating, formatSessionDuration, getSessionDurationMinutes } from '@utils/sessionDisplay'

interface SessionCardProps {
  session: SmokingSession
  onEdit?: (session: SmokingSession) => void
  onDelete?: (sessionId: string) => void
  showActions?: boolean
}

const SessionCard: React.FC<SessionCardProps> = ({
  session,
  onEdit,
  onDelete,
  showActions = true
}) => {
  const navigate = useNavigate()
  const effectiveRating = getEffectiveSessionRating(session)
  const durationMinutes = getSessionDurationMinutes(session)

  const getQualityColor = (quality?: string) => {
    switch (quality) {
      case 'excellent': return 'success'
      case 'good': return 'info'
      case 'fair': return 'warning'
      case 'poor': return 'error'
      default: return 'default'
    }
  }

  const handleCardClick = () => {
    navigate(`/app/journal/${session.id}`)
  }

  const thumbnailUrl = session.imageUrls && session.imageUrls.length > 0 ? session.imageUrls[0] : null
  const isActive = session.isDraft && session.startTime && !session.endTime

  return (
    <Card sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      ...(isActive ? {
        border: '2px solid',
        borderColor: 'warning.main',
        boxShadow: (theme) => `0 0 12px ${theme.palette.warning.light}`,
      } : {})
    }}>
      <CardActionArea onClick={handleCardClick} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        {/* Active smoking banner */}
        {isActive && (
          <Box
            sx={{
              bgcolor: 'warning.main',
              color: 'warning.contrastText',
              px: 2,
              py: 0.75,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <SmokingIcon fontSize="small" />
            <Typography variant="subtitle2" fontWeight="bold">
              Currently Smoking
            </Typography>
          </Box>
        )}

        {/* Thumbnail Image */}
        {thumbnailUrl && (
          <CardMedia
            component="img"
            height="140"
            image={thumbnailUrl}
            alt={`${session.cigar.cigarLine.brand.name} ${session.cigar.cigarLine.name}`}
            sx={{ objectFit: 'cover' }}
          />
        )}

        <CardContent sx={{ flexGrow: 1, pb: showActions ? 0 : 2 }}>
          {/* Draft badge (non-active drafts only) */}
          {session.isDraft && !isActive && (
            <Chip label="Draft" color="warning" size="small" sx={{ mb: 1 }} />
          )}

          {/* Header with cigar info and rating */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <Box flex={1} mr={1}>
              <Typography variant="h6" component="h3" sx={{ lineHeight: 1.3, fontSize: isActive ? '1.1rem' : '1rem', fontWeight: isActive ? 'bold' : 'medium' }}>
                {session.cigar.cigarLine.brand.name} {session.cigar.cigarLine.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {session.cigar.vitola.name}
              </Typography>
            </Box>
            {effectiveRating != null && (
              <Box display="flex" flexDirection="column" alignItems="flex-end">
                <Rating value={effectiveRating} precision={0.5} size="small" readOnly />
                <Typography variant="caption" color="text.secondary">
                  {effectiveRating}/5
                </Typography>
              </Box>
            )}
          </Box>

          {/* Session details */}
          <Stack spacing={0.5} mb={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <TimeIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {format(new Date(session.smokedDate), 'MMM dd, yyyy')} • {formatSessionDuration(durationMinutes)}
              </Typography>
            </Box>

            {session.location && (
              <Box display="flex" alignItems="center" gap={1}>
                <LocationIcon fontSize="small" color="action" />
                <Typography variant="body2" noWrap>{session.location}</Typography>
              </Box>
            )}

            {session.weather && (
              <Box display="flex" alignItems="center" gap={1}>
                <WeatherIcon fontSize="small" color="action" />
                <Typography variant="body2" noWrap>{session.weather}</Typography>
              </Box>
            )}

            {session.pairing && (
              <Box display="flex" alignItems="center" gap={1}>
                <PairingIcon fontSize="small" color="action" />
                <Typography variant="body2" noWrap>{session.pairing}</Typography>
              </Box>
            )}

            {session.companions && session.companions.length > 0 && (
              <Box display="flex" alignItems="center" gap={1}>
                <PeopleIcon fontSize="small" color="action" />
                <Typography variant="body2" noWrap>
                  {session.companions.slice(0, 2).join(', ')}
                  {session.companions.length > 2 && ` +${session.companions.length - 2}`}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Quality chips */}
          {(session.drawQuality || session.burnQuality) && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" mb={1}>
              {session.drawQuality && (
                <Chip
                  label={`Draw: ${session.drawQuality}`}
                  size="small"
                  color={getQualityColor(session.drawQuality) as any}
                  variant="outlined"
                />
              )}
              {session.burnQuality && (
                <Chip
                  label={`Burn: ${session.burnQuality}`}
                  size="small"
                  color={getQualityColor(session.burnQuality) as any}
                  variant="outlined"
                />
              )}
            </Stack>
          )}

          {/* Tasting notes preview */}
          {session.tastingNotes && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontStyle: 'italic',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                &ldquo;{session.tastingNotes}&rdquo;
              </Typography>
            </>
          )}

          {/* Additional images count */}
          {session.imageUrls && session.imageUrls.length > 1 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              +{session.imageUrls.length - 1} more photo{session.imageUrls.length > 2 ? 's' : ''}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>

      {/* Actions */}
      {showActions && (
        <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
          {onEdit && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onEdit(session) }}
              color="primary"
              aria-label="Edit session"
            >
              <EditIcon />
            </IconButton>
          )}
          {onDelete && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDelete(session.id) }}
              color="error"
              aria-label="Delete session"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </CardActions>
      )}
    </Card>
  )
}

export default SessionCard
